/**
 * TradeStack - Customer Detail Page
 */

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useConfig } from '../context/ConfigContext';
import api from '../api';
import './CustomerDetail.css';

function CustomerDetail() {
  const { id } = useParams();
  const { formatCurrency, formatDate } = useConfig();

  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadCustomer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadCustomer = async () => {
    try {
      const response = await api.getCustomer(id);
      setCustomer(response.data.customer);
    } catch (err) {
      setError('Failed to load customer');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="customer-detail-page"><div className="loading-state">Loading...</div></div>;
  }

  if (error || !customer) {
    return (
      <div className="customer-detail-page">
        <div className="error-state">
          {error || 'Customer not found'}
          <Link to="/customers" className="btn btn-secondary">Back to Customers</Link>
        </div>
      </div>
    );
  }

  const displayName = customer.company_name || `${customer.first_name} ${customer.last_name}`;

  return (
    <div className="customer-detail-page">
      {/* Header */}
      <div className="detail-header">
        <div className="header-left">
          <Link to="/customers" className="back-link">&larr; Back to Customers</Link>
          <div className="customer-header-info">
            <div className="customer-avatar large">
              {displayName[0].toUpperCase()}
            </div>
            <div>
              <h1>{displayName}</h1>
              {customer.company_name && customer.first_name && (
                <span className="customer-contact-name">
                  {customer.first_name} {customer.last_name}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="header-actions">
          <Link to={`/customers/${id}/edit`} className="btn btn-secondary">Edit</Link>
          <Link to={`/jobs/new?customerId=${id}`} className="btn btn-secondary">New Job</Link>
          <Link to={`/estimates/new?customerId=${id}`} className="btn btn-primary">Create Estimate</Link>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="customer-stats-bar">
        <div className="stat-item">
          <span className="stat-value">{customer.job_count || 0}</span>
          <span className="stat-label">Total Jobs</span>
        </div>
        <div className="stat-item">
          <span className="stat-value">{formatCurrency(customer.total_revenue || 0)}</span>
          <span className="stat-label">Total Revenue</span>
        </div>
        <div className="stat-item">
          <span className="stat-value">{formatCurrency(customer.outstanding_balance || 0)}</span>
          <span className="stat-label">Outstanding</span>
        </div>
        <div className="stat-item">
          <span className="stat-value">{customer.last_job_date ? formatDate(customer.last_job_date) : 'Never'}</span>
          <span className="stat-label">Last Job</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="detail-tabs">
        <button
          className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={`tab-btn ${activeTab === 'jobs' ? 'active' : ''}`}
          onClick={() => setActiveTab('jobs')}
        >
          Jobs
        </button>
        <button
          className={`tab-btn ${activeTab === 'invoices' ? 'active' : ''}`}
          onClick={() => setActiveTab('invoices')}
        >
          Invoices
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'overview' && (
          <div className="overview-content">
            <div className="info-grid">
              <div className="info-card">
                <h3>Contact Information</h3>
                <div className="info-rows">
                  {customer.email && (
                    <div className="info-row">
                      <span className="info-label">Email</span>
                      <a href={`mailto:${customer.email}`} className="info-value link">
                        {customer.email}
                      </a>
                    </div>
                  )}
                  {customer.phone && (
                    <div className="info-row">
                      <span className="info-label">Phone</span>
                      <a href={`tel:${customer.phone}`} className="info-value link">
                        {customer.phone}
                      </a>
                    </div>
                  )}
                  {customer.alt_phone && (
                    <div className="info-row">
                      <span className="info-label">Alt Phone</span>
                      <a href={`tel:${customer.alt_phone}`} className="info-value link">
                        {customer.alt_phone}
                      </a>
                    </div>
                  )}
                </div>
              </div>

              <div className="info-card">
                <h3>Address</h3>
                <div className="info-rows">
                  {customer.address_line1 ? (
                    <div className="info-row">
                      <span className="info-value">
                        {customer.address_line1}<br />
                        {customer.city && `${customer.city}, `}
                        {customer.state} {customer.zip}
                      </span>
                    </div>
                  ) : (
                    <div className="info-row">
                      <span className="info-value text-muted">No address on file</span>
                    </div>
                  )}
                </div>
              </div>

              {customer.notes && (
                <div className="info-card full-width">
                  <h3>Notes</h3>
                  <p className="customer-notes">{customer.notes}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'jobs' && (
          <div className="jobs-content">
            <div className="section-header">
              <h3>Jobs History</h3>
              <Link to={`/jobs/new?customerId=${id}`} className="btn btn-secondary">
                + New Job
              </Link>
            </div>
            {(!customer.jobs || customer.jobs.length === 0) ? (
              <div className="empty-tab">
                <p>No jobs yet for this customer</p>
                <Link to={`/jobs/new?customerId=${id}`} className="btn btn-primary">
                  Create First Job
                </Link>
              </div>
            ) : (
              <div className="jobs-list compact">
                {customer.jobs.map(job => (
                  <Link to={`/jobs/${job.id}`} key={job.id} className="job-row">
                    <div className="job-info">
                      <span className="job-title">{job.title}</span>
                      <span className="job-date">{formatDate(job.created_at)}</span>
                    </div>
                    <div className="job-meta">
                      <span className="job-value">{formatCurrency(job.total_value || 0)}</span>
                      <span className={`status-badge status-${job.status}`}>{job.status}</span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'invoices' && (
          <div className="invoices-content">
            <div className="section-header">
              <h3>Invoices</h3>
              <Link to={`/invoices/new?customerId=${id}`} className="btn btn-secondary">
                + New Invoice
              </Link>
            </div>
            {(!customer.invoices || customer.invoices.length === 0) ? (
              <div className="empty-tab">
                <p>No invoices yet for this customer</p>
              </div>
            ) : (
              <div className="invoices-list">
                {customer.invoices.map(invoice => (
                  <Link to={`/invoices/${invoice.id}`} key={invoice.id} className="invoice-row">
                    <div className="invoice-info">
                      <span className="invoice-number">{invoice.invoice_number}</span>
                      <span className="invoice-date">{formatDate(invoice.invoice_date)}</span>
                    </div>
                    <div className="invoice-meta">
                      <span className="invoice-amount">{formatCurrency(invoice.total)}</span>
                      <span className={`status-badge status-${invoice.status}`}>{invoice.status}</span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default CustomerDetail;
